/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal for provider selection.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAiProviders } from '../api/aiProvidersApi';

interface QuickListProvider {
  id?: number;
  name?: string;
  provider?: string;
  base_url?: string | null;
}

export function useQuickList(mode: 'create' | 'edit', id: string | undefined, duplicateId: string | null) {
  const navigate = useNavigate();

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProviders, setQuickListProviders] = useState<QuickListProvider[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Load quick list providers
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;
    
    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const response = await getAiProviders({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
        tenantSlug: undefined,
        userId: undefined
      });

      let allProviders = Array.isArray(response.data) ? response.data : [];
      
      // If we got 100 results, try to load more pages
      if (allProviders.length === 100 && response.pagination) {
        const totalPages = response.pagination.totalPages || Math.ceil((response.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) { // Limit to 10 pages max (1000 providers)
          const nextResponse = await getAiProviders({
            page,
            limit: 100,
            sortBy: 'name',
            sortOrder: 'asc',
            tenantSlug: undefined,
            userId: undefined
          });
          
          if (Array.isArray(nextResponse.data)) {
            allProviders = [...allProviders, ...nextResponse.data];
          }
        }
      }
      
      setQuickListProviders(allProviders);
    } catch (error) {
      setQuickListProviders([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProviders.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListProviders.length, loadQuickList]);

  // Filter providers client-side
  const filteredQuickList = useMemo(() => {
    const providers = quickListProviders.filter(p => p.id != null);
    if (!quickListSearch.trim()) {
      return providers;
    }
    const searchLower = quickListSearch.toLowerCase();
    return providers.filter(
      (provider) =>
        provider.name?.toLowerCase().includes(searchLower) ||
        provider.provider?.toLowerCase().includes(searchLower) ||
        provider.base_url?.toLowerCase().includes(searchLower)
    );
  }, [quickListProviders, quickListSearch]);

  // Handle item select
  const handleItemSelect = useCallback((item: QuickListProvider) => {
    setShowQuickList(false);
    if (item.id) {
      navigate(`/admin/ai-integration/providers/${item.id}`);
    }
  }, [navigate]);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId: mode === 'edit' && !duplicateId ? id : null
  };
}

